import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { 
    Typography, Breadcrumbs, Link, Box, Paper, Grid2 as Grid, 
    FormControlLabel, Switch, Button, CircularProgress 
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import API from '@utils/axios';
import { getGenderName } from '@utils/helpers';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useDialog } from '@contexts/DialogContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import Layout from '@components/Layout';
import SendoutName from '@components/sendout/SendoutName';
import SendoutContactStatusChip from '@components/sendout/SendoutContactStatusChip';
import ContactCard from '@components/contact/ContactCard';
import ContactAudits from '@components/contact/ContactAudits';
import ContactSendoutExtra from '@components/contact/ContactSendoutExtra';

function SendoutEditPage() {
    const { t } = useTranslation();
    const { showMessage } = useSnackbar();
    const { requestConfirm } = useDialog();
    const { sendoutId, contactId } = useParams();
    const [searchParams] = useSearchParams();
    const [sendoutData, setSendoutData] = useState(null);
    const [contactData, setContactData] = useState({});
    const [extra, setExtra] = useState({});
    const [audits, setAudits] = useState([]);
    const [actions, setActions] = useState([]);
    const [currentAction, setCurrentAction] = useState(null);
    const [showAuditDetails, setShowAuditDetails] = useLocalStorage('contact-audits.details', false);
    const tab = searchParams.get('tab') || 'status.all';
    const autoOpenMessageId = parseInt(searchParams.get('message'));

    const fetchSendoutData = useCallback(async () => {
        try {
            const response = await API.get(`/sendouts/${sendoutId}`);
            setSendoutData(response.data);
        } catch (error) {
            console.error(error);
        }
    }, [sendoutId]);

    const fetchContactData = useCallback(async () => {
        try {
            const response = await API.get(`/sendouts/${sendoutId}/contacts/${contactId}`);
            setContactData(response.data.contact);
            setExtra(response.data.extra);
            setAudits(response.data.audits);
            setActions(response.data.actions);
        } catch (error) {
            showMessage(t('Internal error.'), 'error');
            console.error(error);
        } finally {
            setCurrentAction(null);
        }
    }, [sendoutId, contactId, showMessage, t]);

    const handleAction = async (action) => {
        requestConfirm({
            message: t('Are you sure you want to perform action "{{action}}"?', { action: action.name }),
            onConfirm: async () => {
                setCurrentAction(action.name);
                // Appeler action.url en POST avec comme contenu RAW action.name
                try {
                    const response = await API.post(action.url, action.name, {
                        headers: {
                            'Content-Type': 'text/plain'
                        }
                    });
                    if (response.status === 200 && response.data.success) {
                        showMessage(t('Action performed successfully'), 'success');
                    } else {
                        showMessage(t('Internal error.'), 'error');
                        console.error(response.data);
                    }
                } catch (error) {
                    showMessage(t('Error performing action'), 'error');
                    console.error(error.message);
                    setCurrentAction(null);
                } finally {
                    fetchContactData();
                }
            }
        });
    };

    useEffect(() => {
        fetchSendoutData();
        fetchContactData();
    }, [fetchSendoutData, fetchContactData]);

    return (
        <Layout>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link component={RouterLink} to="/sendouts" underline="hover">{t('Sendout tracking')}</Link>
                    <Link component={RouterLink} to={`/sendouts/${sendoutId}?tab=${encodeURIComponent(tab)}`} underline="hover">
                        {sendoutData && <SendoutName row={sendoutData} links={true} />}
                    </Link>
                    <Typography>{contactData.first_name
                        ? getGenderName(t, contactData.gender) + ` ${contactData.first_name} ${contactData.last_name}`
                        : ''}</Typography>
                </Breadcrumbs>
                <Link component={RouterLink} to={`/sendouts/${sendoutId}?tab=${encodeURIComponent(tab)}`} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowBackIcon sx={{ mr: 0.5 }} />
                    {t('Back to list')}
                </Link>
            </Box>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, xl: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <ContactCard contact={contactData}>
                            <SendoutContactStatusChip status={contactData.status} />
                        </ContactCard>
                        {actions && actions.length > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                {actions.map((action, index) => (
                                    <Button
                                        key={index}
                                        variant="contained"
                                        color="primary"
                                        disabled={currentAction !== null}
                                        sx={{ mr: 2 }}
                                        startIcon={currentAction === action.name
                                            ? <CircularProgress size={20} color="inherit" />
                                            : <ArrowForwardIcon />}
                                        onClick={() => handleAction(action)}>
                                        {action.name}
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Paper>
                    {extra && (
                        <Paper sx={{ p: 2, mt: 2 }}>
                            <Typography variant="h2" sx={{ fontWeight: 400, mb: 2 }}>{t('Form completed')}</Typography>
                            <ContactSendoutExtra extra={extra} />
                        </Paper>
                    )}
                </Grid>
                <Grid size={{ xs: 12, xl: 6 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h2" sx={{ ml: '28%', transform: 'translateX(-17%)', display: 'inline-block', fontWeight: 400 }}>
                            {t('History')}
                            <FormControlLabel label={t('Details')} sx={{ ml: 2 }} control={
                                <Switch checked={showAuditDetails} onChange={(event) => setShowAuditDetails(event.target.checked)} />
                            } />
                        </Typography>
                        {audits &&
                            <ContactAudits 
                                audits={audits} 
                                showDetails={showAuditDetails} 
                                reloadContact={fetchContactData} 
                                autoOpenId={autoOpenMessageId} />
                        }
                    </Paper>
                </Grid>
            </Grid>
        </Layout>
    );
}

export default SendoutEditPage;
